import { WeeklyPlan, WeeklyAllocation } from '../types';

export const PLAN_STATUS_RANK: Record<string, number> = {
    'En Planificación': 1,
    'En Ejecución': 2,
    'En Revisión': 3,
    'Cerrada': 4
};

export const getPlanStatusRank = (status?: string): number => {
    if (!status) return 0;
    return PLAN_STATUS_RANK[status] || 0;
};

/**
 * Campos de estado de ejecución de una asignación semanal.
 */
export const MANUAL_STATUS_FIELDS: (keyof WeeklyAllocation)[] = [
    'executed',
    'percentCumplimiento',
    'cncId',
    'comentario',
];

const allocKey = (a: WeeklyAllocation) => `${a.taskId}__${a.unitId}`;

/**
 * Obtiene la marca de tiempo más representativa de una asignación.
 * Prefiere `updatedAt`, luego `statusUpdatedAt`. Si no tiene ninguna, retorna 0.
 */
export const getAllocTimestamp = (a?: WeeklyAllocation): number => {
    if (!a) return 0;
    const tUpdated = a.updatedAt ? Date.parse(a.updatedAt) : NaN;
    const tStatus = a.statusUpdatedAt ? Date.parse(a.statusUpdatedAt) : NaN;
    if (!isNaN(tUpdated) && !isNaN(tStatus)) return Math.max(tUpdated, tStatus);
    if (!isNaN(tUpdated)) return tUpdated;
    if (!isNaN(tStatus)) return tStatus;
    return 0;
};

/**
 * Sello de estado manual específico (para campos de ejecución).
 */
export const getStatusTimestamp = (a?: WeeklyAllocation): number => {
    if (!a) return 0;
    const t = a.statusUpdatedAt ? Date.parse(a.statusUpdatedAt) : NaN;
    if (!isNaN(t)) return t;
    return getAllocTimestamp(a);
};

/**
 * Combina el estado local con el remoto (servidor).
 * Reglas fundamentales para evitar pérdida de datos:
 * 1. Para cada plan semanal existente, se procesan todas las asignaciones existentes (tanto locales como remotas).
 * 2. Si una asignación existe en ambos (local y remoto):
 *    - Si la versión remota tiene un sello más reciente (getAllocTimestamp), se priorizan los datos remotos (responsable, días, etc.).
 *    - Si los campos de estado (executed, etc.) tienen un sello manual remoto más reciente, se toman los campos remotos.
 *    - Si la versión remota tiene estados modificados (ej: ejecutado !== null) y la versión local no tiene timestamp o está pendiente sin timestamp, SE CONSERVA LO REMOTO.
 * 3. Si una asignación existe en el remoto pero no en el local, SE PRESERVA (para no borrar tareas agregadas por otros usuarios/dispositivos).
 * 4. Los planes creados en el servidor que no existan localmente se agregan automáticamente.
 */
export function mergeWeeklyPlans(local: WeeklyPlan[], remote: WeeklyPlan[]): WeeklyPlan[] {
    if (!Array.isArray(local) || local.length === 0) return Array.isArray(remote) ? remote : [];
    if (!Array.isArray(remote) || remote.length === 0) return local;

    const remotePlansMap = new Map<number, WeeklyPlan>();
    remote.forEach(p => p && remotePlansMap.set(p.id, p));

    const mergedPlans: WeeklyPlan[] = [];
    const processedPlanIds = new Set<number>();

    // 1. Procesar planes locales (combinándolos con su par remoto si existe)
    for (const lPlan of local) {
        if (!lPlan) continue;
        processedPlanIds.add(lPlan.id);
        const rPlan = remotePlansMap.get(lPlan.id);

        if (!rPlan || !Array.isArray(rPlan.allocations)) {
            mergedPlans.push(lPlan);
            continue;
        }

        // Combinar asignaciones por clave (taskId + unitId)
        const lAllocsMap = new Map<string, WeeklyAllocation>();
        (lPlan.allocations || []).forEach(a => a && lAllocsMap.set(allocKey(a), a));

        const rAllocsMap = new Map<string, WeeklyAllocation>();
        (rPlan.allocations || []).forEach(a => a && rAllocsMap.set(allocKey(a), a));

        const mergedAllocs: WeeklyAllocation[] = [];
        const processedAllocKeys = new Set<string>();

        // 1a. Iterar asignaciones locales
        for (const [key, lAlloc] of lAllocsMap.entries()) {
            processedAllocKeys.add(key);
            const rAlloc = rAllocsMap.get(key);

            if (!rAlloc) {
                // Solo existe localmente (recién agregada)
                mergedAllocs.push(lAlloc);
                continue;
            }

            const lTime = getAllocTimestamp(lAlloc);
            const rTime = getAllocTimestamp(rAlloc);
            const lStatusTime = getStatusTimestamp(lAlloc);
            const rStatusTime = getStatusTimestamp(rAlloc);

            // Base de la asignación: si la remota es más reciente en estructura, tomamos remota como base
            let base: WeeklyAllocation = { ...(rTime > lTime ? rAlloc : lAlloc) };

            // Protección de RESPONSABLE:
            // 1. Si ambos tienen responsable, prevalece el que tenga la marca de tiempo más reciente (rTime > lTime o lTime > rTime).
            // 2. Si uno tiene responsable y el otro no (o viene vacío), se mantiene el responsable existente.
            if (rAlloc.responsibleId && lAlloc.responsibleId) {
                if (rTime > lTime) {
                    base.responsibleId = rAlloc.responsibleId;
                } else {
                    base.responsibleId = lAlloc.responsibleId;
                }
            } else if (rAlloc.responsibleId && !lAlloc.responsibleId) {
                base.responsibleId = rAlloc.responsibleId;
            } else if (lAlloc.responsibleId && !rAlloc.responsibleId) {
                base.responsibleId = lAlloc.responsibleId;
            }

            // Protección de ESTADO (executed, percentCumplimiento, cncId, comentario):
            // Regla cronológica estricta: prevalece el cambio manual más reciente (lStatusTime vs rStatusTime).
            // Esto permite que una tarea vuelva válidamente de 'No ejecutado' (executed: false) a 'Pendiente' (executed: null)
            // sin que el estado calificado anterior del servidor lo revierta.
            const rHasExecutedState = rAlloc.executed !== null && rAlloc.executed !== undefined;
            const lHasExecutedState = lAlloc.executed !== null && lAlloc.executed !== undefined;

            if (lStatusTime > rStatusTime) {
                // Local es estrictamente más reciente -> Prevalece LOCAL
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (lAlloc as any)[f];
                });
                base.statusUpdatedAt = lAlloc.statusUpdatedAt || lAlloc.updatedAt;
                base.statusUpdatedBy = lAlloc.statusUpdatedBy || lAlloc.updatedBy;
            } else if (rStatusTime > lStatusTime) {
                // Remoto es estrictamente más reciente -> Prevalece REMOTO
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (rAlloc as any)[f];
                });
                base.statusUpdatedAt = rAlloc.statusUpdatedAt || rAlloc.updatedAt;
                base.statusUpdatedBy = rAlloc.statusUpdatedBy || rAlloc.updatedBy;
            } else if (rHasExecutedState && !lHasExecutedState) {
                // Tiempos iguales o sin timestamps: si uno está calificado y el otro es legado/sin calificar, gana el calificado remoto
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (rAlloc as any)[f];
                });
                base.statusUpdatedAt = rAlloc.statusUpdatedAt || rAlloc.updatedAt;
                base.statusUpdatedBy = rAlloc.statusUpdatedBy || rAlloc.updatedBy;
            } else if (!rHasExecutedState && lHasExecutedState) {
                // Tiempos iguales o sin timestamps: gana el calificado local
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (lAlloc as any)[f];
                });
                base.statusUpdatedAt = lAlloc.statusUpdatedAt || lAlloc.updatedAt;
                base.statusUpdatedBy = lAlloc.statusUpdatedBy || lAlloc.updatedBy;
            } else {
                // Tiempos iguales y ambos con igual tipo de estado: prevalece local
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (lAlloc as any)[f];
                });
                base.statusUpdatedAt = lAlloc.statusUpdatedAt || lAlloc.updatedAt;
                base.statusUpdatedBy = lAlloc.statusUpdatedBy || lAlloc.updatedBy;
            }

            // Preservar la marca de tiempo más alta en la asignación combinada
            const finalTime = Math.max(lTime, rTime, lStatusTime, rStatusTime);
            if (finalTime > 0) {
                base.updatedAt = new Date(finalTime).toISOString();
            }

            mergedAllocs.push(base);
        }

        // Combinar registro de supresiones (deletedAllocations) de local y remoto (prevalece el timestamp de borrado más reciente)
        const combinedDeletedAllocations: { [key: string]: string } = {
            ...(rPlan.deletedAllocations || {}),
            ...(lPlan.deletedAllocations || {})
        };
        // Para claves comunes, asegurar la marca más reciente
        if (rPlan.deletedAllocations && lPlan.deletedAllocations) {
            for (const [delKey, rDelTimeStr] of Object.entries(rPlan.deletedAllocations)) {
                const lDelTimeStr = lPlan.deletedAllocations[delKey];
                if (lDelTimeStr) {
                    const rDelTime = Date.parse(rDelTimeStr) || 0;
                    const lDelTime = Date.parse(lDelTimeStr) || 0;
                    combinedDeletedAllocations[delKey] = rDelTime > lDelTime ? rDelTimeStr : lDelTimeStr;
                }
            }
        }

        // 1b. Conservar asignaciones que existían en el remoto y no estaban en local,
        // SIEMPRE QUE NO hayan sido eliminadas más recientemente según combinedDeletedAllocations
        for (const [key, rAlloc] of rAllocsMap.entries()) {
            if (!processedAllocKeys.has(key)) {
                const delTimeStr = combinedDeletedAllocations[key];
                const delTime = delTimeStr ? (Date.parse(delTimeStr) || 0) : 0;
                const rAllocTime = getAllocTimestamp(rAlloc);

                if (delTime > 0 && delTime >= rAllocTime) {
                    // La asignación fue eliminada legítimamente de manera posterior o concurrente a su última edición
                    continue;
                }
                mergedAllocs.push(rAlloc);
            }
        }

        // El estado del plan (En Planificación / En Ejecución / En Revisión / Cerrada):
        // REGLA CRÍTICA DE BLINDAJE:
        // 1. Un plan 'Cerrada' es un hito de auditoría finalizado.
        //    Bajo NINGUNA circunstancia un cliente con datos desactualizados o una asignación pendiente
        //    puede degradar automáticamente un plan 'Cerrada' a 'En Ejecución' o 'En Revisión'.
        //    Solo una acción explícita de reapertura ('reopenedAt' posterior a 'closedAt') puede cambiarlo.
        const lClosedTime = lPlan.closedAt ? Date.parse(lPlan.closedAt) : (lPlan.status === 'Cerrada' && lPlan.updatedAt ? Date.parse(lPlan.updatedAt) : 0);
        const rClosedTime = rPlan.closedAt ? Date.parse(rPlan.closedAt) : (rPlan.status === 'Cerrada' && rPlan.updatedAt ? Date.parse(rPlan.updatedAt) : 0);
        const lReopenedTime = lPlan.reopenedAt ? Date.parse(lPlan.reopenedAt) : 0;
        const rReopenedTime = rPlan.reopenedAt ? Date.parse(rPlan.reopenedAt) : 0;
        const maxReopenedTime = Math.max(lReopenedTime, rReopenedTime);
        const maxClosedTime = Math.max(lClosedTime, rClosedTime);

        const lPlanStatusTime = lPlan.statusUpdatedAt ? Date.parse(lPlan.statusUpdatedAt) : (lPlan.updatedAt ? Date.parse(lPlan.updatedAt) : 0);
        const rPlanStatusTime = rPlan.statusUpdatedAt ? Date.parse(rPlan.statusUpdatedAt) : (rPlan.updatedAt ? Date.parse(rPlan.updatedAt) : 0);

        let finalStatus = lPlan.status;
        let finalStatusUpdatedAt = lPlan.statusUpdatedAt || lPlan.updatedAt;
        let finalClosedAt = lPlan.closedAt || rPlan.closedAt;
        let finalClosedBy = lPlan.closedBy || rPlan.closedBy;
        let finalReopenedAt = lPlan.reopenedAt || rPlan.reopenedAt;
        let finalReopenedBy = lPlan.reopenedBy || rPlan.reopenedBy;

        const isClosedInEither = lPlan.status === 'Cerrada' || rPlan.status === 'Cerrada';

        if (isClosedInEither) {
            if (maxReopenedTime > maxClosedTime) {
                // Se reabrió deliberadamente con una acción posterior al cierre
                finalStatus = (lReopenedTime >= rReopenedTime ? lPlan.status : rPlan.status) || 'En Revisión';
                if (finalStatus === 'Cerrada') finalStatus = 'En Revisión';
                finalStatusUpdatedAt = new Date(maxReopenedTime).toISOString();
            } else {
                // Permanece estrictamente Cerrada
                finalStatus = 'Cerrada';
                finalStatusUpdatedAt = maxClosedTime > 0 ? new Date(maxClosedTime).toISOString() : (lPlan.updatedAt || rPlan.updatedAt);
                if (rPlan.status === 'Cerrada' && (!lPlan.closedAt || rClosedTime >= lClosedTime)) {
                    finalClosedAt = rPlan.closedAt || rPlan.updatedAt;
                    finalClosedBy = rPlan.closedBy || rPlan.updatedBy;
                } else if (lPlan.status === 'Cerrada') {
                    finalClosedAt = lPlan.closedAt || lPlan.updatedAt;
                    finalClosedBy = lPlan.closedBy || lPlan.updatedBy;
                }
            }
        } else {
            // REGLA UNIVERSAL CRONOLÓGICA (Regla 11 AGENTS.md):
            // Siempre prevalece el cambio más reciente en el tiempo (lPlanStatusTime vs rPlanStatusTime).
            // Si el usuario hace click en "Editar Planificación", ese cambio tiene un timestamp nuevo (nowIso)
            // y DEBE respetarse. La madurez de avance (rank) solo se utiliza como desempate cuando
            // los timestamps son idénticos o no existen.
            if (lPlanStatusTime > rPlanStatusTime) {
                // El cambio local es más reciente en el tiempo
                finalStatus = lPlan.status;
                finalStatusUpdatedAt = lPlan.statusUpdatedAt || lPlan.updatedAt;
            } else if (rPlanStatusTime > lPlanStatusTime) {
                // El servidor tiene un cambio más reciente en el tiempo
                finalStatus = rPlan.status;
                finalStatusUpdatedAt = rPlan.statusUpdatedAt || rPlan.updatedAt;
            } else {
                // Empate o sin timestamps: desempate por madurez operativa
                const lRank = getPlanStatusRank(lPlan.status);
                const rRank = getPlanStatusRank(rPlan.status);
                if (rRank > lRank) {
                    finalStatus = rPlan.status;
                    finalStatusUpdatedAt = rPlan.statusUpdatedAt || rPlan.updatedAt;
                } else if (lRank > rRank) {
                    finalStatus = lPlan.status;
                    finalStatusUpdatedAt = lPlan.statusUpdatedAt || lPlan.updatedAt;
                } else {
                    finalStatus = lPlan.status || rPlan.status;
                    finalStatusUpdatedAt = lPlan.statusUpdatedAt || lPlan.updatedAt || rPlan.updatedAt;
                }
            }
        }

        const lPlanTime = lPlan.updatedAt ? Date.parse(lPlan.updatedAt) : 0;
        const rPlanTime = rPlan.updatedAt ? Date.parse(rPlan.updatedAt) : 0;
        let highestAllocTime = 0;
        mergedAllocs.forEach(a => {
            const t = getAllocTimestamp(a);
            if (t > highestAllocTime) highestAllocTime = t;
        });

        // La marca de tiempo del plan debe representar cambios reales en el plan o sus asignaciones
        const finalPlanTime = Math.max(lPlanTime, rPlanTime, highestAllocTime);

        mergedPlans.push({
            ...lPlan,
            status: finalStatus,
            statusUpdatedAt: finalStatusUpdatedAt,
            ...(finalClosedAt ? { closedAt: finalClosedAt } : {}),
            ...(finalClosedBy ? { closedBy: finalClosedBy } : {}),
            ...(finalReopenedAt ? { reopenedAt: finalReopenedAt } : {}),
            ...(finalReopenedBy ? { reopenedBy: finalReopenedBy } : {}),
            allocations: mergedAllocs,
            ...(Object.keys(combinedDeletedAllocations).length > 0 ? { deletedAllocations: combinedDeletedAllocations } : {}),
            ...(finalPlanTime > 0 ? { updatedAt: new Date(finalPlanTime).toISOString() } : {})
        });
    }

    // 2. Incorporar planes que existían en el remoto y no en el local
    for (const rPlan of remote) {
        if (rPlan && !processedPlanIds.has(rPlan.id)) {
            mergedPlans.push(rPlan);
        }
    }

    // Ordenar planes por id ascendente
    mergedPlans.sort((a, b) => a.id - b.id);
    return mergedPlans;
}
